import { isEmpty, sortBy, forEach, filter, reduce, map } from 'lodash-es';
import { getDuration } from '~/utils/time_utils';

export default {
  data() {
    return {
      datetimeRange: { start: moment(), stop: moment() },
      duration: null,
      inputtedDuration: null,
      extraPickerClasses: 'form-group string required',
      projectId: null,
      userId: null,
      taskId: null,
      workBreakPolicyId: null,
      validationToken: null,
      saving: false,
      check: {},
      workNote: '',
      note: '',
      billable: true,
      errorMessage: 'There was an error when saving this time entry',
      successMessage: 'Time entry saved',
    };
  },
  mounted() {
    this.$refs.dialogObserver.reset();
  },
  computed: {
    oneDay() {
      return moment.duration(1, 'day');
    },
    oneHour() {
      return moment.duration(1, 'hour');
    },
    timeEntryParams() {
      return {
        user_id: this.user.id,
        // TODO: this.project should be always defined, this is a workaround for the tests
        organization_id: this.project?.organization_id,
        source_organization_id: this.organizationId,
        needs_html: false,
        time_entry: {
          project_id: this.projectId,
          task_id: this.taskId,
          work_note: this.workNote,
          note: this.note,
          start_time: this.datetimeRange.start.format(),
          stop_time: this.datetimeRange.stop.format(),
          billable: this.billable,
          manual_time_request: this.manualTimeRequest,
        },
      };
    },
    workBreakTimeEntryParams() {
      return {
        user_id: this.user.id,
        organization_id: this.workBreakPolicy.organization_id,
        needs_html: false,
        time_entry: {
          project_id: this.workBreakProjectId,
          start_time: this.datetimeRange.start.format(),
          stop_time: this.datetimeRange.stop.format(),
          work_break_policy_id: this.workBreakPolicyId,
        },
      };
    },
  },
  methods: {
    now() {
      return moment().tz(this.currentZone.name);
    },
    setStartStopDatetime(startDatetime, stopDatetime) {
      this.datetimeRange = { start: startDatetime, stop: stopDatetime };
    },
    totalDuration(timeEntries) {
      return reduce(
        timeEntries,
        (sum, timeEntry) => {
          const duration = getDuration(timeEntry.start_time, timeEntry.stop_time);

          return sum.add(duration);
        },
        moment.duration(0),
      );
    },
    timeEntriesOnDate(datetime) {
      const timeEntries = map(this.blockingTimeEntries, (timeEntry) => {
        return { start_time: timeEntry.start_time, stop_time: timeEntry.stop_time };
      });
      const timeEntriesOnDate = filter(timeEntries, (timeEntry) => {
        return datetime.isSame(timeEntry.start_time, 'day');
      });

      return timeEntriesOnDate;
    },
    // Set the start and stop time of the form based on existing time entries.
    // This method gets the existing time entries and tries to get a start and stop time that fits between them.
    // @param useFixedDuration {Boolean} When it's true we will always try to find a time that fits the
    // proposedDuration. When it's false we will try to find a time that fits a slot that can be smaller than the
    // proposedDration
    // @param proposedDuration {moment.Duration} - proposed duration for the timespan, defaut to one hour
    // @returns Nothing
    setInitialStartStopTimes(useFixedDuration = false, proposedDuration = this.oneHour) {
      let lowerLimit = this.datetimeRange.start.clone().hours(8).minutes(0).seconds(0).milliseconds(0);
      let setSuccessfully = false;
      const isToday = this.isToday(lowerLimit);
      const upperLimit = isToday
        ? this.now().clone().seconds(0).milliseconds(0)
        : this.datetimeRange.start.clone().hours(23).minutes(59).seconds(59).milliseconds(0);
      const timeEntriesOnDate = this.timeEntriesOnDate(lowerLimit);

      if (isEmpty(timeEntriesOnDate) || this.totalDuration(timeEntriesOnDate) >= this.oneDay) {
        if (isToday) {
          if (upperLimit - lowerLimit < proposedDuration) {
            this.setStartStopDatetime(upperLimit.clone().subtract(proposedDuration), upperLimit);
          } else {
            this.setStartStopDatetime(lowerLimit, lowerLimit.clone().add(proposedDuration));
          }
        } else if (lowerLimit < upperLimit) {
          this.setStartStopDatetime(lowerLimit, lowerLimit.clone().add(proposedDuration));
        } else {
          this.setStartStopDatetime(upperLimit.clone().subtract(proposedDuration), upperLimit);
        }
      } else {
        let candidateDatetime = lowerLimit.clone();
        let stopDatetime = null;
        const sortedTimeEntries = sortBy(timeEntriesOnDate, [
          (timeEntry) => {
            const startDatetime = timeEntry.start_time;

            return startDatetime;
          },
        ]);
        let timeEntriesAfterLowerLimit = filter(sortedTimeEntries, (timeEntry) => {
          const startDatetime = timeEntry.start_time;
          const stopDatetime = timeEntry.stop_time;
          const isAfterStart =
            startDatetime.isAfter(lowerLimit, 'second') || startDatetime.isSame(lowerLimit, 'second');
          const isAfterStop = stopDatetime.isAfter(lowerLimit, 'second') || stopDatetime.isSame(lowerLimit, 'second');

          return isAfterStart || isAfterStop;
        });

        if (isEmpty(timeEntriesAfterLowerLimit) && upperLimit > lowerLimit) {
          if (isToday && upperLimit - lowerLimit < proposedDuration) {
            this.setStartStopDatetime(lowerLimit, upperLimit);

            return;
          }
          this.setStartStopDatetime(lowerLimit, lowerLimit.clone().add(proposedDuration));

          return;
        }

        forEach(timeEntriesAfterLowerLimit, (timeEntry) => {
          if (stopDatetime) {
            return;
          }
          const timeEntryStartDatetime = timeEntry.start_time;
          const duration = getDuration(candidateDatetime, timeEntryStartDatetime);

          if (duration > moment.duration(0) && (!useFixedDuration || duration >= proposedDuration)) {
            if (duration > proposedDuration) {
              stopDatetime = candidateDatetime.clone().add(proposedDuration);
            } else {
              stopDatetime = timeEntryStartDatetime;
            }
            this.setStartStopDatetime(candidateDatetime, stopDatetime);
            setSuccessfully = true;
          } else {
            candidateDatetime = timeEntry.stop_time;
          }
        });

        if (isToday && !setSuccessfully) {
          const duration = getDuration(candidateDatetime, upperLimit);

          if (duration > moment.duration(0)) {
            if (duration > proposedDuration) {
              stopDatetime = candidateDatetime.clone().add(proposedDuration);
            } else if (!useFixedDuration) {
              stopDatetime = upperLimit;
            }
            if (stopDatetime) {
              this.setStartStopDatetime(candidateDatetime, stopDatetime);
              setSuccessfully = true;
            }
          }
        }

        let candidateStopDatetime = candidateDatetime.clone().add(proposedDuration);

        if (!stopDatetime && candidateStopDatetime.isBefore(upperLimit, 'second')) {
          this.setStartStopDatetime(candidateDatetime, candidateStopDatetime);
          setSuccessfully = true;
        }
        if (setSuccessfully) {
          return;
        }

        lowerLimit = lowerLimit.hours(0).minutes(0).seconds(0);
        candidateDatetime = lowerLimit.clone();
        timeEntriesAfterLowerLimit = filter(sortedTimeEntries, (timeEntry) => {
          const startDatetime = timeEntry.start_time;

          return startDatetime.isAfter(lowerLimit, 'second') || startDatetime.isSame(lowerLimit, 'second');
        });

        forEach(timeEntriesAfterLowerLimit, (timeEntry) => {
          if (stopDatetime) {
            return;
          }
          const timeEntryStartDatetime = timeEntry.start_time;
          const duration = getDuration(candidateDatetime, timeEntryStartDatetime);

          if (duration > moment.duration(0)) {
            if (duration > proposedDuration) {
              stopDatetime = candidateDatetime.clone().add(proposedDuration);
            } else {
              stopDatetime = timeEntryStartDatetime;
            }
            this.setStartStopDatetime(candidateDatetime, stopDatetime);
          } else {
            candidateDatetime = timeEntry.stop_time;
          }
        });

        candidateStopDatetime = candidateDatetime.clone().add(proposedDuration);
        if (!stopDatetime && candidateStopDatetime.isBefore(upperLimit, 'second')) {
          this.setStartStopDatetime(candidateDatetime, candidateStopDatetime);
        }
      }
    },
  },
};
